import { useMemo, useState } from 'react';
import { BarChart } from '../../charts/BarChart';
import { RankedBars } from '../../charts/RankedBars';
import { Card, ChartCard, DataTable, Stat, StatusChip, type Column } from '../../components/data';
import { ErrorBanner, Loading } from '../../components/ui';
import { formatCompactCurrency, num, pct, periodLabel, relativeDays, snakeLabel } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { Employee } from '../../lib/types';
import {
  poBand,
  type ClientOption,
  type ProcurementData,
  type PurchaseOrderRow,
  type VendorOption,
  ProductOption,
} from './adminTypes';
import { RegisterPanel, WindowPicker, formatIsoShort, shareBand, type RegisterSpec } from './adminUi';

const PO_CATEGORY = ['hardware', 'software', 'licensing', 'services', 'consumables'] as const;
const PO_STATUS = [
  'quoting', 'awaiting_approval', 'ordered', 'partially_received', 'received', 'cancelled',
] as const;
const PO_EXCEPTION = ['damaged', 'short_ship', 'wrong_item', 'missing_docs'] as const;

const WINDOWS = [
  { id: '90', label: '90 days' },
  { id: '180', label: '6 months' },
  { id: '365', label: '12 months' },
];

/**
 * Procurement and receiving.
 *
 * Every measure on this tab is a gap between two of the four dates a purchase
 * order carries — quote requested, quote sent, ordered, promised, received. That
 * is why they are five separate nullable columns rather than a status and one
 * timestamp: a status tells you where an order is now, and none of these
 * questions are about now.
 *
 * Note which population each measure runs over, because it is the difference
 * between a useful number and a flattering one. On-time delivery is over orders
 * both promised AND received: an order with no promised date cannot be late, and
 * counting it as on time would inflate the figure with orders nobody committed
 * to a date for.
 */
export function AdminProcurement({
  canWrite,
  onChanged,
}: {
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [window, setWindow] = useState(90);
  const analytics = useResource<ProcurementData>(`/admin/analytics/procurement?days=${window}`);
  const vendors = useResource<VendorOption[]>('/admin/pickers/vendors');
  const clients = useResource<ClientOption[]>('/admin/pickers/clients');
  const products = useResource<ProductOption[]>('/admin/pickers/products');
  const employees = useResource<Employee[]>('/employees?status=active');

  const spec = useMemo<RegisterSpec<PurchaseOrderRow>>(
    () => ({
      resource: 'purchase-orders',
      title: 'Purchase orders',
      singular: 'Purchase order',
      emptyTitle: 'No purchase orders recorded yet',
      emptyBody:
        'Record what has been ordered and when it was promised. The four dates are what make this register worth keeping: quote turnaround, order-to-receipt and the slip against the promised date all compute from them, and none of them can be recovered after the fact.',
      searchHint: 'PO number, description, client or vendor',
      idOf: (row) => row.PurchaseOrderId,
      labelOf: (row) => `${row.PoNumber} — ${row.Description}`,
      filters: [
        { param: 'status', label: 'Status', options: PO_STATUS },
        { param: 'category', label: 'Category', options: PO_CATEGORY },
        { param: 'exception', label: 'Exception', options: PO_EXCEPTION },
      ],
      columns: [
        {
          key: 'PoNumber',
          label: 'PO',
          render: (row) => (
            <div>
              <div className="admin-cell-title">{row.PoNumber}</div>
              <div className="xs muted">{row.Description}</div>
            </div>
          ),
        },
        {
          key: 'VendorName',
          label: 'Vendor / client',
          render: (row) => (
            <div>
              <div>{row.VendorName ?? <span className="sub">no vendor</span>}</div>
              {row.ClientName && <div className="xs muted">for {row.ClientName}</div>}
            </div>
          ),
        },
        {
          key: 'Status',
          label: 'Status',
          render: (row) => (
            <div className="admin-due">
              <StatusChip band={poBand(row.Status)}>{snakeLabel(row.Status)}</StatusChip>
              {row.ReceivingException && (
                <StatusChip band="serious">{snakeLabel(row.ReceivingException)}</StatusChip>
              )}
            </div>
          ),
        },
        {
          key: 'PromisedDate',
          label: 'Promised',
          render: (row) => {
            if (!row.PromisedDate) return <span className="sub">—</span>;
            return (
              <div>
                <div>{formatIsoShort(row.PromisedDate)}</div>
                {/* DaysOverdue is null once received, however late it arrived —
                    a closed order is history, and flagging it forever would bury
                    the ones somebody can still chase. */}
                {row.DaysOverdue !== null && row.DaysOverdue > 0 && (
                  <div className="xs admin-warn-text">{relativeDays(-row.DaysOverdue)}</div>
                )}
                {row.ReceiptSlipDays !== null && (
                  <div className="xs muted">
                    {row.ReceiptSlipDays > 0
                      ? `${row.ReceiptSlipDays}d late`
                      : row.ReceiptSlipDays < 0
                        ? `${Math.abs(row.ReceiptSlipDays)}d early`
                        : 'on the day'}
                  </div>
                )}
              </div>
            );
          },
        },
        {
          key: 'QtyOrdered',
          label: 'Qty',
          align: 'right',
          render: (row) => (
            <span className={row.ReceivedDate && row.QtyReceived < row.QtyOrdered ? 'admin-warn-text' : undefined}>
              {row.QtyReceived} / {row.QtyOrdered}
            </span>
          ),
        },
        {
          key: 'CostTotal',
          label: 'Cost',
          align: 'right',
          render: (row) => formatCompactCurrency(row.CostTotal),
        },
        {
          key: 'Margin',
          label: 'Margin',
          align: 'right',
          /* Computed here rather than server-side because it is a ratio of two
             columns already on the row — the browser can divide, and adding a
             column to the SELECT for it would be a second definition to keep in
             step with the roll-up above. */
          render: (row) =>
            row.SellTotal > 0
              ? pct(((row.SellTotal - row.CostTotal) / row.SellTotal) * 100, 0)
              : <span className="sub">—</span>,
        },
      ],
      fields: [
        { name: 'poNumber', label: 'PO number', kind: 'text' },
        { name: 'category', label: 'Category', kind: 'select', options: PO_CATEGORY },
        {
          /* Before Description on purpose: picking the catalogue line is the
             better answer, and Description is what you write when there is
             not one. See migration 102. */
          name: 'productId',
          label: 'Catalogue line',
          kind: 'select',
          optional: true,
          placeholder: 'Not in the price book',
          choices: (products.data ?? []).map((p) => ({
            value: String(p.ProductId),
            label: p.Category ? `${p.Name} · ${p.Category}` : p.Name,
          })),
        },
        { name: 'description', label: 'Description', kind: 'text', wide: true },
        {
          name: 'adminVendorId',
          label: 'Vendor',
          kind: 'select',
          optional: true,
          placeholder: 'Not recorded',
          choices: (vendors.data ?? []).map((v) => ({ value: String(v.AdminVendorId), label: v.Name })),
        },
        {
          name: 'clientId',
          label: 'For client',
          kind: 'select',
          optional: true,
          placeholder: 'Internal',
          choices: (clients.data ?? []).map((c) => ({
            value: String(c.ClientId),
            label: c.IsActive ? c.Name : `${c.Name} (inactive)`,
          })),
        },
        {
          name: 'quoteRequestedDate',
          label: 'Quote requested',
          kind: 'date',
          hint: 'With the date below, this gives quote turnaround.',
        },
        { name: 'quoteSentDate', label: 'Quote sent', kind: 'date' },
        { name: 'orderDate', label: 'Ordered', kind: 'date' },
        {
          name: 'promisedDate',
          label: 'Promised',
          kind: 'date',
          hint: 'The supplier’s committed date. On-time delivery is measured against this.',
        },
        { name: 'receivedDate', label: 'Received', kind: 'date' },
        { name: 'status', label: 'Status', kind: 'select', options: PO_STATUS },
        {
          name: 'receivingException',
          label: 'Receiving exception',
          kind: 'select',
          options: PO_EXCEPTION,
          optional: true,
          placeholder: 'None',
        },
        { name: 'qtyOrdered', label: 'Qty ordered', kind: 'int' },
        { name: 'qtyReceived', label: 'Qty received', kind: 'int' },
        { name: 'costTotal', label: 'Cost total', kind: 'money' },
        { name: 'sellTotal', label: 'Sell total', kind: 'money' },
        { name: 'stockedToDepot', label: 'Booked into the depot', kind: 'checkbox' },
        {
          name: 'buyerEmployeeId',
          label: 'Buyer',
          kind: 'select',
          optional: true,
          placeholder: 'Unassigned',
          choices: (employees.data ?? []).map((e) => ({
            value: String(e.EmployeeId),
            label: e.FullName,
          })),
        },
        { name: 'notes', label: 'Notes', kind: 'textarea', wide: true },
      ],
      blank: {
        poNumber: '',
        category: 'hardware',
        description: '',
        adminVendorId: '',
        clientId: '',
        quoteRequestedDate: '',
        quoteSentDate: '',
        orderDate: '',
        promisedDate: '',
        receivedDate: '',
        status: 'ordered',
        receivingException: '',
        qtyOrdered: 1,
        qtyReceived: 0,
        costTotal: '',
        sellTotal: '',
        stockedToDepot: false,
        buyerEmployeeId: '',
        notes: '',
      },
      toForm: (row) => ({
        poNumber: row.PoNumber,
        category: row.Category,
        description: row.Description,
        adminVendorId: row.AdminVendorId ?? '',
        clientId: row.ClientId ?? '',
        quoteRequestedDate: row.QuoteRequestedDate?.slice(0, 10) ?? '',
        quoteSentDate: row.QuoteSentDate?.slice(0, 10) ?? '',
        orderDate: row.OrderDate?.slice(0, 10) ?? '',
        promisedDate: row.PromisedDate?.slice(0, 10) ?? '',
        receivedDate: row.ReceivedDate?.slice(0, 10) ?? '',
        status: row.Status,
        receivingException: row.ReceivingException ?? '',
        qtyOrdered: row.QtyOrdered,
        qtyReceived: row.QtyReceived,
        costTotal: row.CostTotal,
        sellTotal: row.SellTotal,
        stockedToDepot: row.StockedToDepot,
        buyerEmployeeId: row.BuyerEmployeeId ?? '',
        notes: row.Notes,
      }),
    }),
    [vendors.data, clients.data, employees.data],
  );

  const reload = () => {
    analytics.reload();
    onChanged();
  };

  const d = analytics.data;
  const s = d?.summary;
  const hasOrders = (s?.poCount ?? 0) > 0 || (s?.openPoCount ?? 0) > 0;

  return (
    <div className="admin-stack">
      {analytics.error && <ErrorBanner message={analytics.error} />}
      {!d && analytics.loading && <Loading label="Loading procurement" />}

      {d && s && hasOrders && (
        <>
          <div className="row-between wrap">
            <p className="sub">
              Orders placed in the last {d.window} days. Open orders are shown whatever their age —
              one placed four months ago and still not received is the most important row here.
            </p>
            <WindowPicker value={window} onChange={setWindow} options={WINDOWS} />
          </div>

          <div className="grid grid-4">
            <Stat
              label="On-time delivery"
              value={s.onTimeDeliveryPercent === null ? '—' : pct(s.onTimeDeliveryPercent, 0)}
              foot={
                s.onTimeDeliveryPercent === null
                  ? 'nothing promised and received yet'
                  : `over ${num(s.received)} ${s.received === 1 ? 'receipt' : 'receipts'}`
              }
            />
            <Stat
              label="Quote turnaround"
              value={s.avgQuoteTurnaroundDays === null ? '—' : `${num(s.avgQuoteTurnaroundDays, 1)} d`}
              foot="request to quote sent"
            />
            <Stat
              label="Receiving accuracy"
              value={s.receivingAccuracyPercent === null ? '—' : pct(s.receivingAccuracyPercent, 0)}
              foot={
                s.receivingAccuracyPercent === null
                  ? 'nothing received yet'
                  : 'receipts with no exception'
              }
            />
            <Stat
              label="Gross margin"
              value={s.grossMarginPercent === null ? '—' : pct(s.grossMarginPercent, 1)}
              foot={`${formatCompactCurrency(s.grossProfit)} on ${formatCompactCurrency(s.sellTotal)}`}
            />
          </div>

          <div className="grid grid-4">
            <Stat
              label="Open orders"
              value={s.openPoCount}
              foot={`${formatCompactCurrency(s.openPoValue)} committed`}
            />
            <Stat
              label="Past promise"
              value={s.overdueReceiptCount}
              flagged={s.overdueReceiptCount > 0}
              foot="open and overdue"
            />
            <Stat
              label="Short ships"
              value={s.shortShipRatePercent === null ? '—' : pct(s.shortShipRatePercent, 0)}
              foot="received under the ordered quantity"
            />
            <Stat
              label="Booked to depot"
              value={s.depotStockingPercent === null ? '—' : pct(s.depotStockingPercent, 0)}
              foot="receipts recorded as stocked"
            />
          </div>

          <div className="admin-split">
            <ChartCard
              title="Spend and profit by month"
              subtitle="Cost of goods and the gross profit on top of it."
              footer={
                s.avgOrderToReceiptDays === null
                  ? undefined
                  : `Average order to receipt: ${num(s.avgOrderToReceiptDays, 1)} days.`
              }
              table={{
                columns: MONTHLY_COLUMNS,
                rows: d.monthly,
                rowKey: (row) => row.period,
              }}
            >
              <BarChart
                data={d.monthly}
                xKey="period"
                stacked
                series={[
                  { key: 'costTotal', label: 'Cost of goods', color: 'var(--series-1)' },
                  { key: 'grossProfit', label: 'Gross profit', color: 'var(--series-2)' },
                ]}
                xFormat={periodLabel}
                yFormat={(v) => formatCompactCurrency(v)}
                height={240}
              />
            </ChartCard>

            <ChartCard
              title="Spend by category"
              subtitle="At cost, over the window."
              table={{
                columns: [
                  {
                    key: 'category',
                    label: 'Category',
                    render: (row: ProcurementData['byCategory'][number]) => snakeLabel(row.category),
                  },
                  { key: 'poCount', label: 'Orders', align: 'right' },
                  {
                    key: 'costTotal',
                    label: 'Cost',
                    align: 'right',
                    render: (row: ProcurementData['byCategory'][number]) =>
                      formatCompactCurrency(row.costTotal),
                  },
                ],
                rows: d.byCategory,
                rowKey: (row) => row.category,
              }}
            >
              <RankedBars
                rows={d.byCategory.map((c) => ({
                  label: snakeLabel(c.category),
                  value: c.costTotal,
                  meta: `${c.poCount} ${c.poCount === 1 ? 'order' : 'orders'}`,
                }))}
                format={(v) => formatCompactCurrency(v)}
                labelWidth={132}
              />
            </ChartCard>
          </div>

          <div className="admin-split">
            <ChartCard
              title="Top suppliers by spend"
              subtitle="At cost, over the window."
              table={{
                columns: [
                  { key: 'vendorName', label: 'Vendor' },
                  { key: 'poCount', label: 'Orders', align: 'right' },
                  {
                    key: 'costTotal',
                    label: 'Cost',
                    align: 'right',
                    render: (row: ProcurementData['topVendors'][number]) =>
                      formatCompactCurrency(row.costTotal),
                  },
                ],
                rows: d.topVendors,
                rowKey: (row) => row.vendorName,
              }}
            >
              <RankedBars
                rows={d.topVendors.map((v) => ({
                  label: v.vendorName,
                  value: v.costTotal,
                  meta: `${v.poCount} ${v.poCount === 1 ? 'order' : 'orders'}`,
                }))}
                format={(v) => formatCompactCurrency(v)}
                emptyMessage="No orders in this window carry a vendor."
              />
            </ChartCard>

            <Card
              title="Receiving exceptions"
              subtitle="What went wrong on the way in, over the window."
            >
              {d.exceptions.length === 0 ? (
                <p className="sub">
                  {s.received === 0
                    ? 'Nothing has been received in this window yet.'
                    : `No exceptions across ${num(s.received)} ${s.received === 1 ? 'receipt' : 'receipts'}.`}
                </p>
              ) : (
                <RankedBars
                  rows={d.exceptions.map((e) => ({
                    label: snakeLabel(e.reason),
                    value: e.count,
                    /* An exception is a fault, not a magnitude on a neutral
                       scale, so the whole list takes the status colour. */
                    color: 'var(--status-serious)',
                  }))}
                  format={(v) => num(v)}
                  labelWidth={132}
                />
              )}
            </Card>
          </div>

          <Card
            title="Open orders"
            subtitle="Everything still in flight, soonest promised first. Orders with no promised date are last — nobody committed to one."
          >
            <DataTable
              columns={OPEN_COLUMNS}
              rows={d.openOrders}
              rowKey={(row) => String(row.purchaseOrderId)}
              empty="Nothing is outstanding."
            />
          </Card>
        </>
      )}

      <RegisterPanel spec={spec} canWrite={canWrite} onChanged={reload} />
    </div>
  );
}

const MONTHLY_COLUMNS: Column<ProcurementData['monthly'][number]>[] = [
  { key: 'period', label: 'Month', render: (row) => periodLabel(row.period) },
  { key: 'poCount', label: 'Orders', align: 'right' },
  { key: 'costTotal', label: 'Cost', align: 'right', render: (row) => formatCompactCurrency(row.costTotal) },
  { key: 'sellTotal', label: 'Sell', align: 'right', render: (row) => formatCompactCurrency(row.sellTotal) },
  {
    key: 'grossProfit',
    label: 'Profit',
    align: 'right',
    render: (row) => formatCompactCurrency(row.grossProfit),
  },
];

const OPEN_COLUMNS: Column<ProcurementData['openOrders'][number]>[] = [
  {
    key: 'poNumber',
    label: 'PO',
    render: (row) => (
      <div>
        <div className="admin-cell-title">{row.poNumber}</div>
        <div className="xs muted">{row.description}</div>
      </div>
    ),
  },
  {
    key: 'vendorName',
    label: 'Vendor / client',
    render: (row) => (
      <div>
        <div>{row.vendorName ?? <span className="sub">no vendor</span>}</div>
        {row.clientName && <div className="xs muted">for {row.clientName}</div>}
      </div>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => <StatusChip band={poBand(row.status)}>{snakeLabel(row.status)}</StatusChip>,
  },
  { key: 'orderDate', label: 'Ordered', render: (row) => formatIsoShort(row.orderDate) },
  {
    key: 'promisedDate',
    label: 'Promised',
    render: (row) => {
      if (!row.promisedDate) return <span className="sub">not committed</span>;
      const late = (row.daysOverdue ?? 0) > 0;
      return (
        <div className="admin-due">
          <span>{formatIsoShort(row.promisedDate)}</span>
          <StatusChip band={shareBand(late ? 0 : 100, 100, 50)}>
            {late ? `${row.daysOverdue}d late` : 'on time so far'}
          </StatusChip>
        </div>
      );
    },
  },
  {
    key: 'costTotal',
    label: 'Cost',
    align: 'right',
    render: (row) => formatCompactCurrency(row.costTotal),
  },
];
